/**
 * Platform-admin Themes manager.
 * Create / edit / delete themes (color tokens + font stacks).
 * Seeded themes cannot be deleted.
 */
import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Palette, Plus, Trash, PencilSimple } from "@phosphor-icons/react";

const TOKEN_KEYS = [
    "background", "foreground", "primary", "primary-foreground",
    "secondary", "muted-foreground", "card", "border", "accent", "destructive",
];

const emptyDraft = () => ({
    code: "",
    name: "",
    description: "",
    tokens: Object.fromEntries(TOKEN_KEYS.map((k) => [k, ""])),
    fonts: { body: "Inter, system-ui, sans-serif", display: "Inter, system-ui, sans-serif" },
});

export default function PlatformThemesPage() {
    const [themes, setThemes] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState(emptyDraft());

    const load = async () => {
        try { const { data } = await api.get("/themes"); setThemes(data); }
        catch (e) { toast.error(extractError(e)); }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setDraft(emptyDraft()); setOpen(true); };
    const openEdit = (t) => {
        setEditing(t);
        setDraft({
            code: t.code, name: t.name, description: t.description || "",
            tokens: { ...Object.fromEntries(TOKEN_KEYS.map((k) => [k, ""])), ...(t.tokens || {}) },
            fonts: { body: "", display: "", ...(t.fonts || {}) },
        });
        setOpen(true);
    };

    const save = async () => {
        try {
            if (editing) {
                await api.patch(`/themes/${editing.id}`, {
                    name: draft.name, description: draft.description,
                    tokens: draft.tokens, fonts: draft.fonts,
                });
                toast.success("Theme updated");
            } else {
                if (!draft.code.match(/^[a-z0-9-]+$/)) {
                    toast.error("Code must be lowercase alphanumeric with dashes");
                    return;
                }
                await api.post("/themes", draft);
                toast.success("Theme created");
            }
            setOpen(false); await load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const remove = async (t) => {
        if (t.is_seeded) { toast.error("Cannot delete a seeded theme"); return; }
        if (!confirm(`Delete theme "${t.name}"?`)) return;
        try { await api.delete(`/themes/${t.id}`); toast.success("Theme deleted"); await load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    return (
        <PageContainer>
            <PageHeader
                overline="Platform"
                title="Themes"
                description="Define visual themes users can pick from their profile. Seeded themes are read-only color/font sets."
                actions={<Button onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-theme-btn"><Plus size={16} className="mr-2" /> New theme</Button>}
                testid="themes-header"
            />

            {themes.length === 0 ? (
                <EmptyState icon={Palette} title="No themes yet" testid="themes-empty" />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="themes-grid">
                    {themes.map((t) => {
                        const primary = `hsl(${t.tokens?.primary || "224 100% 33%"})`;
                        const bg = `hsl(${t.tokens?.background || "0 0% 100%"})`;
                        const fg = `hsl(${t.tokens?.foreground || "222 47% 11%"})`;
                        return (
                            <div key={t.id} className="border border-border rounded-sm overflow-hidden" data-testid={`theme-card-${t.code}`}>
                                <div className="h-28" style={{ background: bg, color: fg }}>
                                    <div className="p-4 flex flex-col gap-1.5">
                                        <div className="h-2 w-3/5 rounded-sm" style={{ background: primary }} />
                                        <div className="h-1.5 w-2/3 rounded-sm opacity-30" style={{ background: fg }} />
                                        <div className="h-1.5 w-1/2 rounded-sm opacity-30" style={{ background: fg }} />
                                    </div>
                                </div>
                                <div className="p-4 bg-card">
                                    <div className="flex items-center justify-between mb-1">
                                        <div>
                                            <div className="text-sm font-semibold">{t.name}</div>
                                            <div className="text-[10px] font-mono text-muted-foreground">{t.code}</div>
                                        </div>
                                        {t.is_seeded && <Badge variant="outline" className="rounded-sm font-mono text-[9px]">SEEDED</Badge>}
                                    </div>
                                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{t.description}</p>
                                    <div className="flex items-center gap-1">
                                        <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => openEdit(t)} data-testid={`edit-theme-${t.code}`}>
                                            <PencilSimple size={12} className="mr-1" /> Edit
                                        </Button>
                                        {!t.is_seeded && (
                                            <Button variant="ghost" size="sm" className="rounded-sm text-destructive hover:text-destructive" onClick={() => remove(t)} data-testid={`del-theme-${t.code}`}>
                                                <Trash size={12} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="theme-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? `Edit "${editing.name}"` : "New theme"}</DialogTitle>
                        <DialogDescription>Use HSL "h s% l%" format (e.g. <code>224 100% 33%</code>).</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-overline">Code</Label>
                                <Input value={draft.code} disabled={!!editing} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="my-theme" className="mt-2 rounded-sm font-mono" data-testid="theme-code-input" />
                            </div>
                            <div>
                                <Label className="label-overline">Name</Label>
                                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-2 rounded-sm" data-testid="theme-name-input" />
                            </div>
                        </div>
                        <div>
                            <Label className="label-overline">Description</Label>
                            <Textarea value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={2} className="mt-2 rounded-sm" data-testid="theme-desc-input" />
                        </div>
                        <div>
                            <Label className="label-overline mb-2 block">Colors (HSL)</Label>
                            <div className="grid grid-cols-2 gap-2">
                                {TOKEN_KEYS.map((k) => (
                                    <div key={k}>
                                        <Label className="text-[10px] font-mono text-muted-foreground">{k}</Label>
                                        <div className="flex items-center gap-1 mt-1">
                                            <div className="w-4 h-9 rounded-sm border border-border flex-shrink-0" style={{ background: draft.tokens[k] ? `hsl(${draft.tokens[k]})` : "transparent" }} />
                                            <Input value={draft.tokens[k]} onChange={(e) => setDraft({ ...draft, tokens: { ...draft.tokens, [k]: e.target.value } })} placeholder="0 0% 100%" className="rounded-sm font-mono h-9 text-xs" data-testid={`theme-token-${k}`} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="label-overline">Body font</Label>
                                <Input value={draft.fonts.body} onChange={(e) => setDraft({ ...draft, fonts: { ...draft.fonts, body: e.target.value } })} className="mt-2 rounded-sm font-mono text-xs" data-testid="theme-font-body" />
                            </div>
                            <div>
                                <Label className="label-overline">Display font</Label>
                                <Input value={draft.fonts.display} onChange={(e) => setDraft({ ...draft, fonts: { ...draft.fonts, display: e.target.value } })} className="mt-2 rounded-sm font-mono text-xs" data-testid="theme-font-display" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={save} className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-theme-btn">
                            {editing ? "Save changes" : "Create theme"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
